import React, { useState, useRef, useEffect, useMemo } from 'react';
import { motion } from 'framer-motion';
import { textCss } from '../../../common/Css';
import ClearFilterButton from '../../../common/Buttons/ClearFilterButton';
import Buttons from '../../../common/Buttons';
import ButtonScogoPrimary from '../../../common/Buttons/ButtonScogoPrimary';
import DropdownMenu from '../../../common/MenuDropdown';
import { Button } from '@mui/material';
import { allTab, getBulkAction, getDownloadReportActions, newMainTab } from '../TicketController';
import { isCreateTicketAllowed } from '../../../utils/access';
import { useSelector } from 'react-redux';
import ProjectFilter from './ProjectFilter';
import { GetQueryParam, useQuery } from '../../../utils/common';
import { useSearchParams } from 'react-router-dom';
import { readFilterQueryParams } from '../../../utils/filter';
import { ticketFilterSchema } from '../TicketTable';
import { isMobileView } from '../../../utils/utils';
import AdvanceSearchBox from '../../../common/AdvanceSearchBox';
import SearchBox from '../../../common/SearchBox';

const advancedSearchFields = [
    { label: 'Serial Number', key: 'ticket_serial_number' },
    { label: 'Ticket no.', key: 'ticket_number' },
    { label: 'Site', key: 'ticket_location_external_id' },
    { label: 'Pincode', key: 'ticket_pincode' },
    { label: 'External Ticket ID', key: 'ticket_external_ids' },
];

const ticketSearchAs = [
    { label: 'Ticket No.', key: 'q_t_n' },
    { label: 'External Ticket Id', key: 'q_e_t_i' },
    { label: 'Site Id', key: 'q_e_i' },
    { label: 'Serial Number', key: 'q_s_n' },
    { label: 'Title', key: 'q_title' },
    { label: 'Description', key: 'q_desc' },
    { label: 'LC Name', key: 'q_l_c_n' },
    { label: 'LC No.', key: 'q_l_c_num' },
    { label: 'City', key: 'q_c' },
    { label: 'State', key: 'q_s' },
    { label: 'Pincode', key: 'q_p' },
];


const containerVariants = {
    hidden: { opacity: 0, y: -20 },
    visible: {
        opacity: 1,
        y: 0,
        transition: {
            duration: 0.5,
            staggerChildren: 0.1
        }
    }
};

const itemVariants = {
    hidden: { opacity: 0, x: -20 },
    visible: { opacity: 1, x: 0 }
};

const statusList = [
    {
        id: 'all',
        label: 'All',
        value: 'ALL',
        count_key: 'ALL'
    },
    {
        id: 'new',
        label: 'New',
        value: 'NEW',
        count_key: 'OPEN'
    },
    {
        id: 'open',
        label: 'Open',
        value: 'OPEN',
        count_key: 'OPEN'
    },
    {
        id: 'triggered',
        label: 'Triggered',
        value: 'TRIGGERED',
        count_key: 'TRIGGERED'
    },
    {
        id: 'field_accepted',
        label: 'Field Accepted',
        value: 'FIELD_ACCEPTED',
        count_key: 'FIELD_ACCEPTED'
    },
    {
        id: 'in_progress',
        label: 'In Progress',
        value: 'IN_PROGRESS',
        count_key: 'IN_PROGRESS'
    },
    {
        id: 'hold',
        label: 'Hold',
        value: 'HOLD',
        count_key: 'ON_HOLD'
    },
    {
        id: 'completed',
        label: 'Completed',
        value: 'COMPLETED',
        count_key: 'COMPLETED'
    },
    {
        id: 'closed',
        label: 'Closed',
        value: 'CLOSED',
        count_key: 'CLOSED'
    }
]

const TicketTop = ({
    tabs,
    activeTab,
    searchPlaceholder,
    title,
    clearFilterButton,
    handleClearFilterClick,
    searchTerm,
    setSeachQuery,
    goToTicketTab,
    openRaiseTicket,
    getSelectedTickets
}) => {

    const newLoggedUserData = useSelector(state => state.auth.newLoggedUserData);
    const [showFilterPopup, setShowFilterPopup] = useState(false);
    const [selectedStatus, setSelectedStatus] = useState('all');
    const filterPopupRef = useRef(null);
    const query = useQuery();
    const [searchParams, setSearchParams] = useSearchParams();
    const statusQueryParam = GetQueryParam('status');
    const serviceRequestCountByStatus = useSelector(state => state.serviceRequest.serviceRequestCountByStatus);

    useEffect(() => {
        if (statusQueryParam) {
            const status = statusQueryParam.split(',')[0];
            const findStatus = statusList.find(s => s.value === status);
            if (findStatus) {
                setSelectedStatus(findStatus.id);
            }
        }
    }, [statusQueryParam]);

    const handleStatusSelect = (statusId) => {
        setSelectedStatus(statusId);
        setShowFilterPopup(false);

        let status = '';
        if (statusId === 'all') {
            status = '';
        } else if (statusId === 'open') {
            status = 'OPEN';
        } else if (statusId === 'in_progress') {
            status = 'IN_PROGRESS';
        } else if (statusId === 'hold') {
            status = 'ON_HOLD';
        } else if (statusId === 'closed') {
            status = 'CLOSED';
        } else if (statusId === 'completed') {
            status = 'COMPLETED'
        } else if (statusId === 'field_accepted') {
            status = 'FIELD_ACCEPTED'
        } else if (statusId === 'new') {
            status = 'NEW'
        } else if (statusId === 'triggered') {
            status = 'TRIGGERED'
        }

        const currentParams = {};
        for (const [key, value] of searchParams.entries()) {
            currentParams[key] = value;
        }
        setSearchParams({
            ...currentParams,
            status,
        })
    };

    useEffect(() => {
        let { queryObject } = readFilterQueryParams(ticketFilterSchema, query);
        const statusFromQuery = queryObject?.status?.[0]?.apiValue;
        if (statusFromQuery) {
            setSelectedStatus(statusFromQuery);
        }
    }, [query]);

    useEffect(() => {
        const handleClickOutside = (event) => {
            if (filterPopupRef.current && !filterPopupRef.current.contains(event.target)) {
                setShowFilterPopup(false);
            }
        };

        document.addEventListener('mousedown', handleClickOutside);
        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
        };
    }, []);

    useEffect(() => {
        if (activeTab === allTab.path && selectedStatus !== 'all') {
            const currentParams = {};
            for (const [key, value] of searchParams.entries()) {
                currentParams[key] = value;
            }

            let status = '';
            let milestone_status = '';

            if (selectedStatus === 'open') {
                status = 'OPEN';
            } else if (selectedStatus === 'in_progress') {
                status = 'IN_PROGRESS';
            } else if (selectedStatus === 'hold') {
                status = 'ON_HOLD';
            } else if (selectedStatus === 'closed') {
                status = 'CLOSED';
            } else if (selectedStatus === 'completed') {
                status = 'COMPLETED'
            } else if (selectedStatus === 'field_accepted') {
                status = 'FIELD_ACCEPTED'
            } else if (selectedStatus === 'new') {
                status = 'NEW'
            } else if (selectedStatus === 'triggered') {
                status = 'TRIGGERED'
            }

            if (status && (!currentParams.status || currentParams.status !== status)) {
                let newParams = {
                    ...currentParams,
                };
                if (status) {
                    newParams.status = status;
                }
                if (milestone_status) {
                    newParams.milestone_status = milestone_status;
                }
                setSearchParams(newParams);
            }
        }
    }, [activeTab, selectedStatus, searchParams, setSearchParams]);

    const handleClearFiltersAndResetTab = () => {
        setSelectedStatus('all');
        handleClearFilterClick();
        if (activeTab !== allTab.path) {
            goToTicketTab({ tab: allTab.path });
        }
    };

    const memoizedStatusList = useMemo(() => {
        const userRoles = newLoggedUserData.user.roles.map(role => role.label).map(role => role.toUpperCase());
        return statusList.filter((status) => {
            if (status.id === 'new') {
                return userRoles.includes('CLUSTER') || userRoles.includes('PSP');
            }
            if (status.id === 'open') {
                return !userRoles.includes('CLUSTER') && !userRoles.includes('PSP');
            }
            if (status.id === 'triggered') {
                return newLoggedUserData.org.id === 'org_3J2HSqsgennObvh2PlCFJv3nlFE' || userRoles.includes('SCM') || userRoles.includes('CUSTOMER') || userRoles.includes('CUSTOMER_ADMIN');
            }
            return true;
        });
    }, [newLoggedUserData.user.roles, newLoggedUserData.org.id]);

    const getStatusCount = (countKey) => {
        if (countKey === 'ALL') {
            return serviceRequestCountByStatus?.reduce((sum, item) => sum + (item.count || 0), 0) || 0;
        }
        const statusData = serviceRequestCountByStatus?.find(item => item.status === countKey);
        return statusData?.count || 0;
    };

    const formatCount = (count) => {
        // if (count >= 1000000) {
        //     return (count / 1000000).toFixed(1) + 'M';
        // } else if (count >= 1000) {
        //     return (count / 1000).toFixed(1) + 'K';
        // }
        return count.toString();
    };


    return (
        <>
            <motion.div
                className={`${isMobileView ? 'flex flex-col pl-2' : 'md:flex max-w-full'}`}
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ duration: 0.8 }}
            >
                <motion.div
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.5, delay: 0.3 }}
                >
                    {isMobileView ? <h1 className='text-2xl font-semibold'>{title}</h1> : <h1 className={textCss?.moduleTitle}>{title}</h1>}
                </motion.div>

                <motion.div
                    className={`${isMobileView ? 'flex justify-between items-center ml-auto gap-1' : 'md:flex items-center ml-auto gap-2'}`}
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    transition={{ duration: 0.5, delay: 0.5 }}
                >
                    {(tabs.length !== 0) && activeTab !== newMainTab.path && <ProjectFilter activeTab={activeTab} />}
                    {clearFilterButton && activeTab !== newMainTab.path && (
                        <motion.div
                            animate={{ opacity: 1, y: 0 }}
                            transition={{ duration: 0.3, delay: 0.6 }}
                            whileHover={{ scale: 1.05 }}
                            whileTap={{ scale: 0.95 }}
                        >
                            <ClearFilterButton
                                textOrComponent='Clear Filter'
                                onClick={handleClearFiltersAndResetTab}
                            />
                        </motion.div>
                    )}


                    <motion.div
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.3, delay: 0.7 }}
                    >
                        {activeTab !== newMainTab.path ? <AdvanceSearchBox
                            searchKey={searchTerm}
                            onChange={(event, { removeTheseKeys }) => setSeachQuery(event.target.value, { removeTheseKeys })}
                            placeholder={searchPlaceholder}
                            onCancel={() => setSeachQuery('')}
                            allowAdvancedSearch
                            advancedSearchFields={advancedSearchFields}
                            ticketSearchAs={ticketSearchAs}
                        /> : <SearchBox
                            searchTerm={searchTerm}
                            onChange={(event) => setSeachQuery(event.target.value)}
                            placeholder={searchPlaceholder}
                            onCancel={() => setSeachQuery('')}
                        />}
                    </motion.div>
                </motion.div>
            </motion.div>

            <motion.div
                className={`${isMobileView ? 'flex p-2 justify-between w-full max-h-max' : 'md:flex mt-4 justify-between w-full max-h-max'}`}
                variants={containerVariants}
                initial="hidden"
                animate="visible"
            >
                <div className='flex border-1 shadow-md rounded-md'>
                    {tabs.map((tab, index) => {
                        const isSelected = activeTab === tab.path;
                        const tabLabel = tab.path === allTab.path ?
                            (selectedStatus === 'all' ? 'All' : memoizedStatusList.find(s => s.id === selectedStatus)?.label) :
                            tab.label;

                        return (
                            <motion.div
                                key={tab.path}
                                variants={itemVariants}
                                className={`flex ${isSelected
                                    ? 'bg-scogoorange text-white hover:bg-orange-500'
                                    : 'text-scogogray hover:bg-scogolightblue max-w-max'
                                    } ${index === 0 ? 'rounded-l-md' : index === tabs.length - 1 ? 'rounded-r-md' : 'rounded-none'}`}
                            >
                                <div className="relative" ref={tab.path === allTab.path ? filterPopupRef : null}>
                                    <Buttons
                                        buttonClass="whitespace-nowrap font-medium px-3 py-1 pt-2 text-font13 transition"
                                        onClick={() => {
                                            if (tab.path === allTab.path) {
                                                if (isSelected) {
                                                    setShowFilterPopup(!showFilterPopup);
                                                } else {
                                                    goToTicketTab({ tab: tab.path });
                                                    setShowFilterPopup(false);
                                                }
                                            } else {
                                                goToTicketTab({ tab: tab.path });
                                                setShowFilterPopup(false);
                                            }
                                        }}
                                    >
                                        <div className='flex justify-center items-center'>
                                            {tabLabel}
                                            {tab.path === allTab.path && isSelected && (
                                                // <span className="material-icons ml-1 text-sm">
                                                //     {showFilterPopup ? 'arrow_drop_up' : 'arrow_drop_down'}
                                                // </span>
                                                <span class="material-icons-outlined text-[18px]">
                                                    {showFilterPopup ? "expand_less" : 'expand_more'}
                                                </span>
                                            )}
                                        </div>

                                    </Buttons>

                                    {tab.path === allTab.path && showFilterPopup && isSelected && (
                                        <div className="absolute top-full left-0 mt-2 bg-white rounded-md shadow-lg z-50 min-w-[280px] border border-gray-200">
                                            <div className="py-2">
                                                <h3 className="px-4 py-2 text-sm font-semibold text-gray-700 border-b border-gray-100">Filter by Status</h3>
                                                {memoizedStatusList.map((status) => {
                                                    const count = getStatusCount(status.count_key);
                                                    const formattedCount = formatCount(count);
                                                    return (
                                                        <div
                                                            key={status.id}
                                                            className={`flex items-center px-4 py-3 cursor-pointer hover:bg-gray-50 transition-colors duration-150 ${selectedStatus === status.id ? 'bg-orange-50' : ''}`}
                                                            onClick={() => handleStatusSelect(status.id)}
                                                        >
                                                            <div className={`w-3 h-3 rounded-full mr-3 flex-shrink-0 ${status.id === 'all' ? 'bg-gray-500' :
                                                                (status.id === 'open' || status.id === 'completed' || status.id === 'new') ? 'bg-green-500' :
                                                                    status.id === 'in_progress' ? 'bg-yellow-500' :
                                                                        status.id === 'hold' ? 'bg-scogoprimary' :
                                                                            status.id === 'closed' ? 'bg-red-500' :
                                                                                status.id === 'field_accepted' ? 'bg-indigo-500' : 'bg-gray-500'}`}></div>
                                                            <div className="flex items-center justify-between flex-1">
                                                                <span className={`text-md ${selectedStatus === status.id ? 'text-scogoorange font-medium' : 'text-gray-700'}`}>
                                                                    {status.label}
                                                                </span>
                                                                <div className="flex items-center gap-2">
                                                                    {Array.isArray(serviceRequestCountByStatus) && serviceRequestCountByStatus.length > 0 ? <span className={`text-sm font-semibold px-2 py-1 rounded-full ${selectedStatus === status.id ? 'bg-orange-100 text-scogoorange' : 'bg-gray-100 text-gray-600'}`}>
                                                                        {formattedCount}
                                                                    </span> : null}
                                                                    {selectedStatus === status.id && (
                                                                        <span className="material-icons text-scogoorange text-[14px]">check</span>
                                                                    )}
                                                                </div>
                                                            </div>
                                                        </div>
                                                    );
                                                })}
                                            </div>
                                        </div>
                                    )}
                                </div>
                            </motion.div>
                        );
                    })}
                </div>

                <div className='flex gap-2'>
                    {isCreateTicketAllowed(newLoggedUserData.actions) && <ButtonScogoPrimary buttonClass='rounded-md' buttonIcon='./img/plus.svg' textOrComponent={'New Ticket'} onClick={() => openRaiseTicket()} buttonPadding={'px-3 py-1'} />}
                    {!isMobileView && (<> <RenderBulkActionButton getSelectedTickets={getSelectedTickets} />
                        <RenderReportActionButton getSelectedTickets={getSelectedTickets} /></>)}
                </div>
            </motion.div>
        </>
    );
};

export default TicketTop;





const RenderBulkActionButton = ({ getSelectedTickets }) => {
    const ticketPermissions = useSelector(state => state.serviceRequest.ticketPermissions);
    const newLoggedUserData = useSelector(state => state.auth.newLoggedUserData);
    const bulkActions = getBulkAction({ permissions: ticketPermissions, newLoggedUserData });

    const rendorBulkActionButton = () => {
        return (
            <Button
                id='demo-customized-button'
                aria-haspopup='true'
                variant='contained'
                disableElevation
                endIcon={<span className='material-icons '>keyboard_arrow_down</span>}
                style={{ backgroundColor: '#F48A21', color: 'white', height: '3rem' }}
            >
                Actions
            </Button>
        );
    };

    return (
        <DropdownMenu
            menuData={bulkActions}
            componentOrIcon={rendorBulkActionButton}
            isTableAction={false}
            iconColor={'text-scogoprimary'}
            payload={{
                getSelectedTickets,
                newLoggedUserData
            }}
        />
    )
}

const RenderReportActionButton = ({ getSelectedTickets }) => {
    const newLoggedUserData = useSelector(state => state.auth.newLoggedUserData);
    const bulkActions = getDownloadReportActions({ newLoggedUserData });

    const rendorBulkActionButton = () => {
        return (
            <Button
                id='demo-customized-button'
                aria-haspopup='true'
                variant='contained'
                disableElevation
                endIcon={<span className='material-icons '>keyboard_arrow_down</span>}
                style={{ backgroundColor: '#ffffff', color: '#000000', height: '3rem', border: '1px solid #000000' }}
            >
                Download Report
            </Button>
        );
    };

    return (
        <DropdownMenu
            menuData={bulkActions}
            componentOrIcon={rendorBulkActionButton}
            isTableAction={false}
            iconColor={'text-scogoprimary'}
            payload={{
                getSelectedTickets
            }}
        />
    )
}
